import {buffers, eventChannel} from "redux-saga";
import * as Sfx from "redux-saga/effects";
import root from "window-or-global";

import {isWeb} from "@atg-shared/system";
import {getKambiWidgetApi} from "@atg-sport-shared/kambi-widget-api";
import {
    selectKambiDetails,
    fetchKambiDetails,
} from "@atg-sport-shared/sportsbook-data-access-kambi-details";
import {ODDS_FORMAT_DECIMAL, WAPI} from "@atg-sport-shared/sportsbook-types-kambi";
import {
    KAMBI_BC_ID,
    KAMBI_CONTAINER_ID,
    KAMBI_HIDDEN_CONTAINER_ID,
} from "@atg-sport-shared/sportsbook-utils-constants";
import {isSportsbookPath, loadScript} from "@atg-sport-shared/sportsbook-utils-helpers";
import {
    DEFAULT_CUSTOMER_SETTINGS,
    WIDGET_SETTINGS,
} from "@atg-sport-shared/sportsbook-utils-kambi";

import {kambiClientMessage} from "../actions/kambi.actions";
import {
    BEGIN_KAMBI_LOAD,
    REMOVE_KAMBI,
    beginKambiLoad,
    setIsKambiLoading,
    setKambiHasLoaded,
} from "../actions/kambi.client.actions";

import type {KambiDetailsResponse} from "@atg-sport-shared/sportsbook-types-kambi";
import type {KambiUserSettings} from "@atg-sport-shared/sportsbook-utils-kambi";
import type {EventChannel} from "redux-saga";

/**
 * The Kambi widget is a single instance living on `window`/`document`
 * (`root._kbc`, `#KambiBC`, the bootstrap script), but it is driven by one
 * `kambiClientSaga` per micro-frontend store (sport, horse h2h, ...). Those
 * saga instances run concurrently and would otherwise race on the shared
 * widget — e.g. an outgoing vertical disposing the widget a newly entered
 * vertical just rebuilt.
 *
 * A monotonically increasing generation stored on `window` acts as a
 * single-owner token: the saga that loaded the widget most recently owns it.
 * A saga may only tear the widget down while it is still the current owner, so
 * a stale instance can no longer clobber a widget another vertical has taken
 * over. The token is intentionally a window global because it must be shared
 * across the separate micro-frontend stores.
 */
const kambiOwnership = root as unknown as {atgKambiOwnerGen?: number};

const getKambiOwnerGen = (): number => kambiOwnership.atgKambiOwnerGen ?? 0;

const claimKambiOwnership = (): number => {
    const next = getKambiOwnerGen() + 1;
    kambiOwnership.atgKambiOwnerGen = next;
    return next;
};

const isKambiOwner = (ownershipGen: number): boolean =>
    getKambiOwnerGen() === ownershipGen;

function* hasKambiLoaded(hasLoaded: boolean): Generator<Sfx.Effect, void, any> {
    yield Sfx.put(setKambiHasLoaded(hasLoaded));
    if (isWeb) document.body.classList[hasLoaded ? "add" : "remove"]("kambi-has-loaded");
}

export function* disposeKambi(ownershipGen: number): Generator<Sfx.Effect, boolean, any> {
    // Another vertical's saga has taken over the global Kambi widget since this
    // instance loaded it. Don't tear down a widget we no longer own — just
    // clear local loaded state and bail out.
    if (!isKambiOwner(ownershipGen)) {
        yield Sfx.spawn(hasKambiLoaded, false);
        return false;
    }

    // @ts-expect-error
    // eslint-disable-next-line no-underscore-dangle
    root._kbc?.dispose();

    // @ts-expect-error
    root.KambiWidget = undefined;
    // @ts-expect-error
    // eslint-disable-next-line no-underscore-dangle
    root._kc = undefined;

    document.getElementById("KambiBC")?.remove();
    yield Sfx.spawn(hasKambiLoaded, false);

    if (isSportsbookPath(root.location.pathname)) {
        yield Sfx.put(beginKambiLoad());
    }
    return true;
}

function* setupWAPIRequests() {
    try {
        const kambiWidgetApi: Sfx.SagaReturnType<typeof getKambiWidgetApi> =
            yield Sfx.call(getKambiWidgetApi);

        // Subscription for future changes of betslip state (selected outcomes and tab).
        kambiWidgetApi.request(kambiWidgetApi.BETSLIP_OUTCOMES);
        // Subscription for future changes of bet placement state.
        kambiWidgetApi.request(kambiWidgetApi.PLACE_BET_STATE);
        // Get string with format name, this creates a subscription for future changes.
        kambiWidgetApi.request(kambiWidgetApi.CLIENT_ODDS_FORMAT);
        // Subscription for future updates of stakes on the betslip.
        kambiWidgetApi.request(kambiWidgetApi.BETSLIP_STAKE_UPDATED);
        // Subscription for user data changes.
        kambiWidgetApi.request(kambiWidgetApi.USER_DATA);
        // Subscription for session changes.
        kambiWidgetApi.request(kambiWidgetApi.USER_SESSION_CHANGE);
        // Get object literal with information about the page the widget is rendered on.
        kambiWidgetApi.request(kambiWidgetApi.PAGE_INFO);
        // Get boolean defining the users auth status, this creates a subscription for future changes.
        kambiWidgetApi.request(kambiWidgetApi.USER_LOGGED_IN);
        // Get object literal with versions of the client and wapi supported by the current client.
        kambiWidgetApi.request(kambiWidgetApi.VERSIONS);
        // Get object literal with configurations.
        kambiWidgetApi.request(kambiWidgetApi.CLIENT_CONFIG);

        return eventChannel((emit) => {
            const subscription = kambiWidgetApi.subscribe(emit);
            return subscription.unsubscribe;
        });
    } catch (e: unknown) {
        return false;
    }
}

export const setupKambiComponent = () => {
    let kambiBC = document.getElementById(KAMBI_BC_ID);
    if (!kambiBC) {
        kambiBC = document.createElement("div");
        kambiBC.setAttribute("id", KAMBI_BC_ID);
    }
    kambiBC.setAttribute("data-test-id", "kambi-bc");

    let hiddenKambiContainer = document.getElementById(KAMBI_HIDDEN_CONTAINER_ID);
    if (!hiddenKambiContainer) {
        hiddenKambiContainer = document.createElement("div");
        hiddenKambiContainer.style.display = "none";
        hiddenKambiContainer.setAttribute("id", KAMBI_HIDDEN_CONTAINER_ID);
        document.body?.append(hiddenKambiContainer);
    }

    const kambiContainer = document.getElementById(KAMBI_CONTAINER_ID);
    if (kambiContainer) kambiContainer.append(kambiBC);
    else hiddenKambiContainer.append(kambiBC);
};

function setupKambiSettings(allowCookies: boolean, kambiDetails: KambiDetailsResponse) {
    // @ts-expect-error
    root["ga-disable-UA-45067452-1"] = true;
    // @ts-expect-error
    root["ga-disable-UA-45067452-4"] = true;
    // @ts-expect-error
    root["ga-disable-G-X57VHJSQ5M"] = true;

    const {kambiEnvironment, currencyCode, locale, market, offering} = kambiDetails;

    const DEFAULT_USER_SETTINGS: KambiUserSettings = {
        oddsFormat: ODDS_FORMAT_DECIMAL,
        currency: currencyCode,
        locale,
        market,
        playerId: "",
        ticket: "",
        streamingAllowedForPlayer: true,
        allowCookies: undefined,
        environment: kambiEnvironment,
        offering,
    };

    // @ts-expect-error
    // eslint-disable-next-line no-underscore-dangle
    root._kc = {
        ...DEFAULT_USER_SETTINGS,
        allowCookies,
    };

    return eventChannel((emit) => {
        // @ts-expect-error
        root.customerSettings = {
            ...DEFAULT_CUSTOMER_SETTINGS,
            notification: (event: unknown) => emit({type: WAPI.NOTIFICATION, event}),
            heartbeat: () => emit({type: WAPI.HEARTBEAT}),
            handle404: (url: string) => emit({type: WAPI.NOT_FOUND, url}),
            showEventStatistics: (eventId: unknown) =>
                emit({type: WAPI.EVENT_STATS, eventId}),
        };

        // @ts-expect-error
        root.widgetSettings = WIDGET_SETTINGS;

        return () => undefined;
    });
}

const getActiveGroups = () => {
    const groups = root.dataLayer.filter((data) => data?.event === "OneTrustLoaded");

    const activeGroups = groups[groups.length - 1]?.OnetrustActiveGroups;

    return activeGroups || "";
};

export function* shouldAllowCookies(): Generator<Sfx.Effect, boolean, any> {
    let allowCookies = null;

    do {
        const trackingModal = document.getElementById("onetrust-banner-sdk");

        if (trackingModal && trackingModal.style.display !== "none") {
            yield Sfx.delay(100);
        } else {
            const activeGroups = yield Sfx.call(getActiveGroups);
            allowCookies = activeGroups.includes("C0002");
        }
    } while (allowCookies === null);

    return allowCookies;
}

export function* kambiMessageSaga(
    channel: EventChannel<any>,
): Generator<Sfx.Effect, void, any> {
    const task = yield Sfx.takeEvery(channel, function* handleMessage(msg) {
        yield Sfx.put(kambiClientMessage(msg));
    });
    yield Sfx.take(REMOVE_KAMBI);
    yield Sfx.cancel(task);
    channel.close();
}

export function* loadKambiClient(): Generator<Sfx.Effect, boolean, any> {
    const allowCookies = yield Sfx.call(shouldAllowCookies);

    const kambiDetails = yield Sfx.select(selectKambiDetails);

    const kambiBootstrapUrl = `${kambiDetails?.kambiBootstrapUrl}/client/${
        kambiDetails?.offering
    }/kambi-bootstrap.js?cb=${Date.now()}`;

    const kambiMessageChannel = setupKambiSettings(allowCookies, kambiDetails);

    yield Sfx.spawn(kambiMessageSaga, kambiMessageChannel);

    yield Sfx.call(loadScript, kambiBootstrapUrl);
    setupKambiComponent();

    const kambiResponseChannel = yield Sfx.call(setupWAPIRequests);

    if (!kambiResponseChannel) return false;

    yield Sfx.spawn(kambiMessageSaga, kambiResponseChannel);

    return true;
}

export function* kambiClientSaga(): Generator<Sfx.Effect, void, any> {
    // Wait for first BEGIN_KAMBI_LOAD action
    yield Sfx.take(BEGIN_KAMBI_LOAD);

    // Fetch Kambi details once at initialization
    yield Sfx.put(fetchKambiDetails());

    // Wait for details to be fetched before proceeding
    yield Sfx.take("kambiDetails/fetchData/fulfilled");

    const startStop = yield Sfx.actionChannel(
        [BEGIN_KAMBI_LOAD, REMOVE_KAMBI],
        buffers.sliding(1),
    );

    while (true) {
        yield Sfx.put(setIsKambiLoading(true));
        // Claim ownership of the global widget for this load. Whoever loads last
        // owns it; earlier instances will skip their teardown (see disposeKambi).
        const ownershipGen = claimKambiOwnership();
        const success = yield Sfx.call(loadKambiClient);
        yield Sfx.put(setIsKambiLoading(false));
        yield Sfx.spawn(hasKambiLoaded, true);

        while (success && (yield Sfx.take(startStop)).type !== REMOVE_KAMBI);
        yield Sfx.call(disposeKambi, ownershipGen);
        while ((yield Sfx.take(startStop)).type !== BEGIN_KAMBI_LOAD);
    }
}
